import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* accommodation-data-store actions */
import * as AccommodationDataStoreActions from 'src/app/features/data-stores/accommodation-data-store/state/accommodation-data-store.actions';

/* api services */
import { AccommodationControllerService } from 'src/app/features/shared/sdk_v1/services';

export const navigatePathAfterCreatingInstance = '/accommodations/';
export const navigatePathAfterUpdatingInstance = '/accommodations/';
@Injectable()
export class AccommodationDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private accommodationApiService: AccommodationControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  GetActiveAccommodationId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccommodationDataStoreActions.InitFetchingActiveAccommodationWithRelations,
      ),
      withLatestFrom(
        this.store.select((state) => state.RouterDataStore.params),
      ),
      /* dataset */
      mergeMap(([action, params]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          params,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
            /* Pre Data Transformation Step 1 ( Map ) */
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;
            /* transform data */
            const transformedData = JSONAtaObject.chain(data).transform(`
              { "id": params.id }
            `);
            return transformedData;
          }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
            /* get value of the passed jsonata object */
            const data = <any>jsonataObj.value;
            return [
              AccommodationDataStoreActions.GetActiveAccommodationWithRelationsById({ payload: data }),
              ];
          }),
    ),
  );

  getAccommodations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccommodationDataStoreActions.GetAccommodations),
      mergeMap((action) => {
        return this.accommodationApiService.find().pipe(
          mergeMap((accommodations) => {
            const payload: any = {
              accommodations: accommodations,
            };
            return [
              AccommodationDataStoreActions.GetAccommodationsSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [AccommodationDataStoreActions.GetAccommodationsFailure()];
          }),
        );
      }),
    ),
  );

  getAccommodationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccommodationDataStoreActions.GetAccommodationById),
      mergeMap((action) => {
        return this.accommodationApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((accommodation) => {
              const payload: any = {
                accommodation: accommodation,
              };
              return [
                AccommodationDataStoreActions.GetAccommodationByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                AccommodationDataStoreActions.GetAccommodationByIdFailure(),
              ];
            }),
          );
      }),
    ),
  );

  getActiveAccommodationWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccommodationDataStoreActions.GetActiveAccommodationWithRelationsById,
      ),
      mergeMap((action) => {
        return this.accommodationApiService
          .findById({
            id: action.payload.id,
            filter: {
              include: ['tour'],
            },
          })
          .pipe(
            mergeMap((accommodation) => {
              const payload: any = {
                activeAccommodation: accommodation,
              };
              return [
                AccommodationDataStoreActions.GetActiveAccommodationByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                AccommodationDataStoreActions.GetActiveAccommodationByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createAccommodation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccommodationDataStoreActions.CreateAccommodation),
      mergeMap((action) => {
        const params = {
          body: action.payload.accommodation,
        };
        return this.accommodationApiService.create(<any>params).pipe(
          mergeMap((accommodation) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              accommodation: accommodation,
            };
            return [
              AccommodationDataStoreActions.CreateAccommodationSuccessful({
                payload,
              }),
              Navigate({
                payload: { path: navigatePathAfterCreatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [AccommodationDataStoreActions.CreateAccommodationFailure()];
          }),
        );
      }),
    ),
  );

  updateAccommodation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccommodationDataStoreActions.UpdateAccommodation),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.accommodationApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [
              AccommodationDataStoreActions.GetAccommodations(),
              Navigate({
                payload: { path: navigatePathAfterUpdatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [AccommodationDataStoreActions.UpdateAccommodationFailure()];
          }),
        );
      }),
    ),
  );

  deleteAccommodation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccommodationDataStoreActions.DeleteAccommodation),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.accommodationApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              AccommodationDataStoreActions.DeleteAccommodationSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [AccommodationDataStoreActions.DeleteAccommodationFailure()];
          }),
        );
      }),
    ),
  );
}
